import { appConfig } from './app.config';

const API_URL = `${appConfig.apiUrl}/api/v2/`;
const API_V1_URL = `${appConfig.apiUrl}/api/`;

/**
 * Map path to app api.
 * @param {string} path Path.
 * @returns {string}
 */
function toApi(path) {
  return new URL(path, API_URL).toString();
}

/**
 * Map path to app api.
 * @param {string} path Path.
 * @returns {string}
 */
function toApiV1(path) {
  return new URL(path, API_V1_URL).toString();
}

/** Urls used within the application. */
export const appUrlsConfig = {

  auth: {
    login: toApi('auth'),
  },


  analytics: {
    overview: toApi('admin/analytics/overview'),
    baskets: toApi('admin/analytics/baskets'),
    daily: toApi('admin/analytics/daily'),
    bulkBuyers: toApi('admin/analytics/bulk-buyers'),
  },

  orders: {
    admin: {
      getOrders: toApi('admin/orders'),
      searchOrders: toApi('admin/orders/search'),
      getOrderDetail: id => toApi(`admin/orders/${id}`),
      updateOrder: id => toApi(`admin/orders/${id}`),
      removeOrder: id => toApi(`admin/orders/${id}`),
      getOrderBundle: id => toApi(`admin/orders/${id}/item-bundles`),
      updateOrderItem: (orderId, orderItemId) => toApi(`admin/orders/${orderId}/items/${orderItemId}`),
      removeOrderItem: (orderId, orderItemId) => toApi(`admin/orders/${orderId}/items/${orderItemId}`),
      createOrder: toApi('admin/orders'),
      payByCard: orderId => toApi(`admin/orders/${orderId}/payments/card`),
      payByCheck: orderId => toApi(`admin/orders/${orderId}/payments/check`),
      payByPayPal: orderId => toApi(`admin/orders/${orderId}/payments/pay-by-paypal`),
      payForFreeOrder: orderId => toApi(`admin/orders/${orderId}/payments/free-check`),
      payLater: id => toApi(`admin/orders/${id}/payments/later`),
      markPaid: orderId => toApi(`admin/orders/${orderId}/payments/mark-paid`),
      payMultipleOrdersByCard: toApi('admin/orders/payments/multiple/pay-by-card'),
      payMultipleOrdersByApplePay: toApi('admin/orders/payments/multiple/pay-by-apple-pay'),
      payMultipleOrdersByPayPal: toApi('admin/orders/payments/multiple/pay-by-paypal'),
      exportOrders: toApi('admin/orders/export/'),
      isUserPandargca: toApi('admin/orders/is-user-pandargca'),
      genInvoicePandargca: toApi('admin/orders/gen-invoice-pandargca'),
      getNotes: id => toApi(`admin/orders/${id}/events`),
      addNote: id => toApi(`admin/orders/${id}/events`),
    },
    createOrder: toApi('orders'),
    getOrders: toApi('orders'),
    checkoutOrderByCardOrApplePay: toApi('orders/checkout'),
  },

  checkout: {
    checkout: toApi('checkout'),
    validateCheckout: toApi('checkout/validate'),
  },

  registrations: {
    admin: {
      getNotes: id => toApi(`admin/registrations/${id}/notes`),
      addNote: id => toApi(`admin/registrations/${id}/notes`),
      display: toApi('admin/registrations/display'),
    },
  },

  packages: {
    getAssignablePackages: toApi('admin/assignments/packages/assignable'),
    getPackagesV1: toApiV1('packages'),
    getPackages: toApi('packages'),
    getAdminPackages: toApi('packages/admin'),
    getPackageDetail: id => toApi(`packages/${id}`),
    removePackageAvailableSeat: id => toApi(`admin/assignments/packages/${id}/remove-seat`),
  },

  examCode: {
    getList: toApi('admin/exam-codes'),
    resendCodes: toApi('admin/exam-codes/resend'),
  },

  proctorURegistration: {
    admin: {
      getRegistrations: toApi('admin/proctoru-registrations'),
      getDetail: id => toApi(`admin/proctoru-registrations/${id}`),
      export: toApi('admin/proctoru-registrations/export'),
    },
    student: {
      getRegistrations: toApi('proctoru-registrations'),
    },
  },

  user: {
    base: toApi('users'),
    profile: toApi('users/me'),
    fmSurvey: toApi('users/me/fm-survey'),
    dashboardAnnouncement: toApi('users/me/dashboard-announcement'),
    updateReminderSettings: toApi('users/me/reminders'),
    courses: id => toApi(`users/${id}/courses`),
    getRegistrationStats: id => toApi(`users/${id}/registration-stats`),
    rememberedInformation: (userId, packageId) => toApi(`users/${userId}/packages/${packageId}/remembered-information`),
    student: {
      updateUser: id => toApi(`users/${id}`),
    },
    admin: {
      resendPasswords: toApi('admin/users/passwords/resend'),
      getUserById: id => toApi(`admin/users/${id}`),
      updateUser: id => toApi(`admin/users/${id}`),
      updateUsers: toApi('admin/users'),
      resetPassword: id => toApi(`admin/users/${id}/password/reset`),
      deactivate: id => toApi(`admin/users/${id}/deactivate`),
      activate: id => toApi(`admin/users/${id}/activate`),
    },
    createCreditCard: toApi('cards'),
    getAuthorizeNetProfile: toApi('authorizenet/profile'),
    recentPurchases: id => toApi(`users/packages/recent-purchases/${id}`),
  },

  group: {
    admin: {
      getGroups: toApi('admin/groups'),
    },
  },

  classRegistrations: {
    admin: {
      getClassRegistrations: toApi('admin/class-registrations'),
      getClassRegistrationDetail: id => toApi(`admin/class-registrations/${id}`),
      export: toApi('admin/class-registrations/export'),
      assign: toApi('admin/class-registrations/assign'),
      assignClass: id => toApi(`admin/assignments/registrations/${id}/assign-class`),
      retest: id => toApi(`admin/class-registrations/${id}/retest`),
      emailCertificate: id => toApi(`class-registrations/${id}/certificates/download`),
      generalSettings: id => toApi(`admin/class-registrations/${id}`),
      setFlag: id => toApi(`admin/registrations/${id}/set-flag`),
      reschedule: id => toApi(`admin/class-registrations/${id}/reschedule`),
      transferClass: id => toApi(`admin/class-registrations/${id}/transfer`),
      cancelClass: id => toApi(`admin/class-registrations/${id}/cancel`),
    },
    student: {
      orderPrintedCopy: id => toApi(`class-registrations/${id}/certificates/order-printed-copy`),
      getClassRegistrations: toApi('class-registrations'),
      getClassRegistrationDetail: id => toApi(`class-registrations/${id}`),
      downloadCertificate: id => toApi(`class-registrations/${id}/certificates/download`),
    },
  },
  cards: {
    setDefault: toApi('cards'),
    removeCard: toApi('cards'),
  },
  learner: {
    create: toApi('admin/learners'),
    importLearners: toApi('admin/learners/import'),
    exportImportLearnersTemplate: toApi('admin/learners/import-sample/download'),
    get: packageId => toApi(`admin/assignments/packages/${packageId}/assignable-learners`),
    getPagination: toApi('admin/learners'),
    activate: toApi('admin/learners/activate'),
    deactivate: toApi('admin/learners/deactivate'),
    display: toApi('admin/learners/display'),
    delete: id => toApi(`admin/learners/${id}`),
    rbs: id => toApi(`admin/learners/${id}/rbs`),
  },

  rbs: {
    verify: toApiV1('server/verify'),
  },

  groupAdmin: {
    groupAdmins: toApi('admin/group-admins'),
  },

  document: {
    generate: toApi('admin/documents'),
    generatePayment: toApi('admin/payment-documents'),
    sendEmail: toApi('admin/documents/send-email'),
    sendPaymentEmail: toApi('admin/payment-documents/send-email'),
  },

  lookup: {
    documentTypes: toApi('lookup/document-types'),
    additionalDocuments: toApi('lookup/additional-documents'),
    courses: toApi('lookup/courses'),
    licenseStatuses: toApi('lookup/statuses'),
    visibilities: toApi('lookup/visible'),
    courseStatuses: toApi('lookup/course-statuses'),
    registrationSources: toApi('lookup/registration-sources'),
  },

  eLearning: {
    getLearningRegistrations: toApi('elearning-registrations'),
    getDetail: id => toApi(`elearning-registrations/${id}`),
    payLaminatedCard: id => toApi(`elearning-registrations/${id}/laminated-cards`),
    assignELearningCourses: id => toApi(`admin/assignments/packages/${id}/assign`),
    quizResults: id => toApi(`admin/elearning-registrations/${id}/quiz-results`),
    quizResult: id => toApi(`admin/elearning-registrations/quiz-results/${id}`),
    quizzes: id => toApi(`admin/elearning-registrations/${id}/quizzes`),
    restartTraining: toApi('admin/elearning-registrations/restart-training'),
    learnerPositions: id => toApi(`admin/elearning-registrations/${id}/learner-positions`),
    updateLearnerPositions: (registrationId, learnerPositionId) => toApi(`admin/elearning-registrations/${registrationId}/learner-positions/${learnerPositionId}`),
    updateLearnerProgress: id => toApi(`admin/elearning-registrations/${id}/slides`),
    emailCertificate: toApi('admin/elearning-registrations/certificates/send-email'),
    downloadCertificates: toApi('admin/elearning-registrations/certificates/download'),
    downloadCertificatesAsZip: toApi('admin/elearning-registrations/certificates/download-as-zip'),
    emailLearners: toApi('admin/elearning-registrations/students/send-email'),
    hideCourses: toApi('admin/elearning-registrations/students/hide'),
    unhideCourses: toApi('admin/elearning-registrations/students/unhide'),
    resendPassword: toApi('admin/elearning-registrations/password/resend'),
    export: toApi('admin/elearning-registrations/export'),
    updateLanguage: (registrationId, languageId) => toApi(`elearning-registrations/${registrationId}/language/${languageId}`),
    transferCourse: id => toApi(`admin/elearning-registrations/${id}/transfer-license`),
    returnLicense: id => toApi(`admin/elearning-registrations/${id}/return-license`),
    generalSettings: id => toApi(`admin/elearning-registrations/${id}/settings`),
    saveCurrentPosition: id => toApi(`admin/elearning-registrations/${id}/learner-positions/save-current-position`),
    batchExtend: toApi('admin/elearning-registrations/batch-extend'),
    extendedList: toApi('admin/elearning-registrations/extended-list'),
  },

  class: {
    getList: toApi('admin/classes/public'),
    getPrivateList: toApiV1('classes/private'),
  },

  state: {
    getList: toApi('states'),
    getFoodManagerStates: toApi('food-manager-states'),
    getAllergenTrainingStates: toApi('allergen-training-states'),
  },

  coursePlayer: {
    getSettings: id => toApi(`elearning-registrations/${id}/player-settings`),
    getDetail: id => toApiV1(`elearning/courses/${id}`),
  },

  exam: {
    get: registrationId => toApi(`elearning-registrations/${registrationId}/exam`),
    startAttempt: registrationId => toApi(`elearning-registrations/${registrationId}/exam/attempts`),
    getAttempt: (registrationId, attemptId) => toApi(`elearning-registrations/${registrationId}/exam/attempts/${attemptId}`),
    saveResponse: (registrationId, attemptId, position) => toApi(
      `elearning-registrations/${registrationId}/exam/attempts/${attemptId}/responses/${position}`,
    ),
    submit: (registrationId, attemptId) => toApi(`elearning-registrations/${registrationId}/exam/attempts/${attemptId}/submit`),
  },

  zip: {
    getDetail: code => toApiV1(`zipcodes/${code}`),
  },

  purchase: {
    orderSummary: toApi('purchase/order-summary'),
  },

  refund: {
    singleRegistration: id => toApi(`admin/registrations/${id}/refund`),
    multipleRegistrations: id => toApi(`admin/orders/${id}/payments/refund`),
    updateRefundCheckNumber: (orderId, paymentId) => toApi(`admin/orders/${orderId}/payments/refund/${paymentId}`),
  },

  applePay: {
    validateMerchant: toApi('apple-pay/validate-merchant'),
    process: toApi('apple-pay/process'),
  },

  paypal: {
    create: toApi('paypal/create'),
  },

  itemBank: {
    sme: {
      me: toApi('item-bank/sme/me'),
      mfaChallenge: toApi('item-bank/sme/mfa/challenge'),
      mfaVerify: toApi('item-bank/sme/mfa/verify'),
      dashboard: toApi('item-bank/sme/dashboard'),
      items: toApi('item-bank/sme/items'),
      itemDetail: id => toApi(`item-bank/sme/items/${id}`),
      guidelineCheck: toApi('item-bank/sme/items/guideline-check'),
      reviewQueue: toApi('item-bank/sme/review-queue'),
      reviewQueueNext: toApi('item-bank/sme/review-queue/next'),
      submitReview: id => toApi(`item-bank/sme/review-queue/${id}/reviews`),
    },
  },
};
